import { useEffect, useRef } from 'react';
import { useHotkeys, useHotkeysContext } from 'react-hotkeys-hook';
import { Key } from 'ts-key-enum';
import { proxy, useSnapshot } from 'valtio';

import { sceneSettingsStore as SceneSettingsStoreType } from 'stores';
import { AnimationPlayerStoreInstance } from 'stores/animationPlayerStoreFactory';
import { ignoreEventWhenSlider as ignoreEventWhen } from 'utils/hotkeys';

export default function usePlayerHotkeys(
  playerName: string,
  animationPlayerStore: AnimationPlayerStoreInstance,
  sceneSettingsStore?: typeof SceneSettingsStoreType,
) {
  const { enableScope, disableScope } = useHotkeysContext();
  const { inputMode } = useSnapshot(animationPlayerStore.state);
  const inputModeBeforeShift = useRef<'rotate' | 'truck' | 'dolly' | undefined>(
    undefined,
  );
  const scope = `${playerName}-player`;

  useEffect(() => {
    if (scope === 'main-player') {
      // Initially enable the main-player scope
      enableScope('main-player');
      return () => disableScope('main-player');
    } else {
      // Once a different player scope is enabled we disable the main-player scope
      disableScope('main-player');
      enableScope(scope);
      return () => {
        // On unmount we enable the main-player scope
        disableScope(scope);
        enableScope('main-player');
      };
    }
  }, []);

  useHotkeys('space', () => animationPlayerStore.playOrPause(), {
    preventDefault: true,
    scopes: [scope],
  });
  useHotkeys(Key.ArrowLeft, () => animationPlayerStore.frameJump(-1), {
    preventDefault: true,
    ignoreEventWhen,
    scopes: [scope],
  });
  useHotkeys(Key.ArrowRight, () => animationPlayerStore.frameJump(1), {
    preventDefault: true,
    ignoreEventWhen,
    scopes: [scope],
  });
  useHotkeys(
    `shift+${Key.ArrowLeft}`,
    () => animationPlayerStore.frameJump(-10),
    { preventDefault: true, ignoreEventWhen },
  );
  useHotkeys(
    `shift+${Key.ArrowRight}`,
    () => animationPlayerStore.frameJump(10),
    { preventDefault: true, ignoreEventWhen, scopes: [scope] },
  );
  useHotkeys(
    `shift+${Key.ArrowRight}`,
    () => animationPlayerStore.frameJump(10),
    { preventDefault: true, ignoreEventWhen, scopes: [scope] },
  );

  useHotkeys(
    'shift',
    (keyboardEvent) => {
      if (keyboardEvent.type === 'keydown') {
        inputModeBeforeShift.current = inputMode;
        animationPlayerStore.setCameraInputMode('truck');
      } else {
        if (inputModeBeforeShift.current) {
          animationPlayerStore.setCameraInputMode(inputModeBeforeShift.current);
          inputModeBeforeShift.current = undefined;
        }
      }
    },
    {
      preventDefault: true,
      keydown: true,
      keyup: true,
      scopes: [scope],
    },
  );
  useHotkeys(`shift+equal`, () => animationPlayerStore.zoomIn(), {
    preventDefault: true,
    scopes: [scope],
  });
  useHotkeys(`shift+minus`, () => animationPlayerStore.zoomOut(), {
    preventDefault: true,
    scopes: [scope],
  });

  useSceneSettingsHotkeys(scope, sceneSettingsStore);
}

const useSceneSettingsHotkeys = (
  scope: string,
  sceneSettingsStore?: typeof SceneSettingsStoreType,
) => {
  const storeSnapshot = useSnapshot(
    sceneSettingsStore?.state ??
      proxy({
        characterSettings: { showSkeleton: false },
        backgroundSettings: { showGrid: false },
      }),
  );

  useHotkeys(
    'shift+g',
    () => {
      if (sceneSettingsStore) {
        sceneSettingsStore.set(
          'backgroundSettings',
          'showGrid',
          !storeSnapshot.backgroundSettings.showGrid,
        );
      }
    },
    {
      preventDefault: true,
      scopes: [scope],
    },
  );
  useHotkeys(
    'shift+m',
    () => {
      if (sceneSettingsStore) {
        sceneSettingsStore.set(
          'characterSettings',
          'showSkeleton',
          !storeSnapshot.characterSettings.showSkeleton,
        );
      }
    },
    {
      preventDefault: true,
      scopes: [scope],
    },
  );
};
